// @erp/module-lib/schemas — the phone-scanner RELAY protocol (SCAN-1). Browser-
// safe: plain data + Web Crypto only (no zod, no Workers types), so ONE contract
// is shared by three consumers — the ScanRelay DO (api lane), the /scan router
// (api lane), and the desk/phone FE (fe lane, ported later). Keeping it zod-free
// keeps the DO bundle lean (the relay coerces bodies defensively, the source's
// idiom) and lets the FE import it without dragging the isolate lane along.
//
// This is a PARALLEL socket, deliberately OUTSIDE the WsShard/Topic cache-
// doorbell tree (ADR-003 §6): it relays decoded barcode strings between a desk
// and its paired phones, NOT thin cache-invalidation events — so its wire
// messages are its OWN protocol and are exempt from the core-schemas WsEvent
// thin-event law (@erp/core-schemas ws.ts). The values it carries are short
// barcode strings, never tenant PII payloads.

/** Drop the pairing code (desk rotates it) after this many bad guesses — brute-force guard. */
export const MAX_PAIR_FAILS = 12;
/** Cap concurrent paired phones per pairing session. */
export const MAX_DEVICES = 8;
/**
 * Reclaim TTL (ms). The ScanRelay DO arms a storage alarm on every op; after this
 * long with NO activity (no op, no connected desk socket) the DO wipes its own
 * storage (code/devices/fails) so an abandoned/attacker-seeded pairing session
 * cannot pin storage forever — the SCAN hostile-verify DoS fix. 30 minutes comfortably
 * outlives a real circulation shift (a live desk socket re-arms it indefinitely).
 */
export const SCAN_SESSION_TTL_MS = 30 * 60_000;
/** Hard cap (chars) on every relayed scan field — a phone-supplied string longer than this is rejected. */
export const MAX_SCAN_FIELD = 512;

/**
 * The WebSocket subprotocol the desk offers, alongside the session id, on
 * `GET /scan/events`. SCAN-3 gate (b): the session id is the sole pairing
 * capability, and carrying it in `?s=` put it in every access log, proxy log,
 * Referer and browser history entry. It now rides `Sec-WebSocket-Protocol`,
 * which is a request HEADER and is not logged by default. The server MUST echo
 * this token back or the browser aborts the connection.
 *
 * Wire shape: `Sec-WebSocket-Protocol: erp-scan-v1, <32-hex session>`.
 */
export const SCAN_WS_PROTOCOL = "erp-scan-v1";

/**
 * Pull the session id out of an offered subprotocol list. Returns "" when the
 * list is absent, lacks the protocol marker, or carries no conforming session —
 * the router 400s on falsy, so a malformed offer is refused rather than guessed.
 */
export const scanSessionFromProtocol = (header: string | null | undefined): string => {
  const parts = String(header ?? "")
    .split(",")
    .map((p) => p.trim())
    .filter(Boolean);
  if (!parts.includes(SCAN_WS_PROTOCOL)) return "";
  for (const part of parts) {
    if (part === SCAN_WS_PROTOCOL) continue;
    const session = sanitizeScanSession(part);
    if (session) return session;
  }
  return "";
};

/** The relay operations a desk/phone POSTs to /scan/<op>. */
export const SCAN_OPS = ["host", "pair", "resume", "send", "unpair"] as const;
export type ScanOp = (typeof SCAN_OPS)[number];

/** What a decoded scan is classified as (fe resolveScan) — 'unknown' is a real, sent kind. */
export const SCAN_KINDS = ["card", "book", "unknown"] as const;
export type ScanKind = (typeof SCAN_KINDS)[number];

/**
 * A paired phone as the DO stores it. `token` is the bearer secret the phone
 * keeps (48 hex / 192 bits) — it NEVER leaves the DO toward a desk; `pub()`
 * strips it to DeviceInfo before any desk fan-out.
 */
export interface Device {
  id: string;
  token: string;
  ua: string;
  ts: number;
}

/** The token-stripped device view safe to share with desks. */
export type DeviceInfo = Pick<Device, "id" | "ua" | "ts">;

/** server→desk: the current device roster + the shared one-time pairing code. */
export interface DevicesMessage {
  t: "devices";
  devices: DeviceInfo[];
  /** the desk should mint & register a fresh code (the old one was consumed). */
  needsCode: boolean;
  code: string | null;
}

/** server→desk: a phone published a decoded scan. */
export interface ScanMessage {
  t: "scan";
  deviceId: string;
  kind?: ScanKind;
  value?: string;
  raw?: string;
  format?: string;
  ts: number;
}

export type ScanServerMessage = DevicesMessage | ScanMessage;

/** A phone-supplied scan after the relay has validated + bounded it (the desk only ever sees this). */
export interface SanitizedScan {
  kind: ScanKind;
  value: string;
  raw: string;
  format: string | null;
}

/**
 * Validate + bound a phone's `send` body before it is fanned out to the desk
 * (the SCAN hostile-verify type-confusion / XSS-depth / DoS-amplification fix).
 * The relay forwarded `kind`/`value`/`raw`/`format` VERBATIM, so a hostile phone
 * could hand the desk a non-string `value` (crashes the desk's `resolveScan(raw)`
 * `.trim()`), a 5000-char blob (fan-out amplification), or a script-bearing `raw`
 * (XSS depth). Contract: `kind` MUST be one of SCAN_KINDS; `value`/`raw`/`format`
 * MUST be strings no longer than MAX_SCAN_FIELD. Anything else ⇒ `null` (reject).
 * `raw` defaults to `value` and `format` to `null` when absent, so the pre-hardening
 * phones that sent only `{kind,value}` still relay. The desk receives ONLY this shape.
 */
export const sanitizeScanPayload = (body: {
  kind?: unknown;
  value?: unknown;
  raw?: unknown;
  format?: unknown;
}): SanitizedScan | null => {
  const { kind, value, raw, format } = body;
  if (typeof kind !== "string" || !(SCAN_KINDS as readonly string[]).includes(kind)) return null;
  if (typeof value !== "string" || value.length === 0 || value.length > MAX_SCAN_FIELD) return null;
  if (raw !== undefined && (typeof raw !== "string" || raw.length > MAX_SCAN_FIELD)) return null;
  if (format !== undefined && format !== null && (typeof format !== "string" || format.length > MAX_SCAN_FIELD))
    return null;
  return {
    kind: kind as ScanKind,
    value,
    raw: typeof raw === "string" ? raw : value,
    format: typeof format === "string" ? format : null,
  };
};

// ── Session + code minters (Web Crypto — identical semantics in browser & workerd) ──

/**
 * A per-PAIRING-SESSION id: a fresh, unguessable random per pairing act (the
 * DO instance name is `scan:<session>`). This REPLACES the shippra source's
 * deriveSession = SHA-256(userId) — a userId is not high-entropy, and a stable
 * deterministic hash is replayable/guessable and cross-desk-shared (ADR-003 §6:
 * ScanRelay is pairing-session-scoped, NOT user-scoped). Minted by the desk.
 */
export const makeScanSession = (): string => {
  // `crypto.randomUUID` exists ONLY in a SECURE CONTEXT (https, or localhost).
  // The desk is very often on a plain-HTTP LAN address — that is the whole point
  // of the feature, since the phone has to reach the desk over the network — and
  // there the property is simply undefined, which threw "crypto.randomUUID is
  // not a function" the moment a pairing panel mounted.
  //
  // `crypto.getRandomValues` has NO secure-context restriction, so the fallback
  // below is the same CSPRNG and the same 122 bits, formatted as an RFC 4122 v4.
  // It is NOT the source's `Math.random` fallback, which decisions.md removed
  // deliberately: this session id is the relay's SOLE bearer capability, so
  // degrading its entropy would quietly gut the security model.
  if (typeof crypto.randomUUID === "function") return crypto.randomUUID();
  const b = new Uint8Array(16);
  crypto.getRandomValues(b);
  b[6] = ((b[6] as number) & 0x0f) | 0x40; // version 4
  b[8] = ((b[8] as number) & 0x3f) | 0x80; // variant 10xx
  const hex = Array.from(b, (x) => x.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
};

/** A 6-digit one-time pairing code the desk registers, the phone types once. */
export const makeScanCode = (): string => {
  const a = new Uint32Array(1);
  crypto.getRandomValues(a);
  return String(100000 + ((a[0] as number) % 900000));
};

/**
 * The accepted shape of a sanitized session: exactly the 32 lowercase hex chars a
 * `makeScanSession()` (= `crypto.randomUUID()`) collapses to once its hyphens are
 * stripped — i.e. the full 122-bit bearer capability. Nothing shorter or non-hex.
 */
export const SCAN_SESSION_RE = /^[0-9a-f]{32}$/;

/**
 * Server-side gate for the session id off the wire before it names a DO instance.
 * The session id IS the sole pairing capability (see modules/lib/decisions.md —
 * SCAN capability model), so a weak/attacker-preseeded value must NOT be honoured.
 * PREVIOUSLY this stripped-and-accepted ([A-Za-z0-9]≤64), so `s='1'` was a valid
 * session and `'a-b-c'`/`'abc'` collided onto one DO. NOW it REJECTS anything that
 * is not the randomUUID-hex shape: de-hyphenate + lowercase, then require the
 * 32-hex form (SCAN_SESSION_RE). Non-conforming ⇒ `""` (the router 400s on falsy) —
 * an entropy floor, not a lossy strip. Enforces min ~122 bits and injectivity.
 */
export const sanitizeScanSession = (raw: unknown): string => {
  const s = String(raw ?? "")
    .replace(/[^a-zA-Z0-9]/g, "")
    .toLowerCase();
  return SCAN_SESSION_RE.test(s) ? s : "";
};
